import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import api from './api';

function CreateTaskForm({ onTaskCreated }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [category, setCategory] = useState('AI');
  const [deadlineHours, setDeadlineHours] = useState(1);
  const [onePerExecutor, setOnePerExecutor] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // 🆕 Состояния для белого списка
  const [whitelistUsernames, setWhitelistUsernames] = useState([]);
  const [currentUsernameInput, setCurrentUsernameInput] = useState('');
  const [whitelistErrors, setWhitelistErrors] = useState([]);

  const textareaRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const categoriesList = [
    { value: 'AI', label: 'ИИ и Нейросети', icon: '🤖', color: 'bg-purple-100 text-purple-700' },
    { value: 'MICRO', label: 'Микро-задачи / Клики', icon: '⏱️', color: 'bg-amber-100 text-amber-700' },
    { value: 'TG_SMM', label: 'Telegram / Соцсети / SMM', icon: '📢', color: 'bg-sky-100 text-sky-700' },
    { value: 'COPYWRITE', label: 'Копирайтинг / Тексты / Рерайт', icon: '✍️', color: 'bg-emerald-100 text-emerald-700' },
    { value: 'DESIGN', label: 'Дизайн / Логотипы / Графика', icon: '🎨', color: 'bg-pink-100 text-pink-700' },
    { value: 'VIDEO', label: 'Видеомонтаж / Монтаж', icon: '🎬', color: 'bg-red-100 text-red-700' },
    { value: 'CRYPTO', label: 'Крипта / Web3 / Блокчейн', icon: '🪙', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'DEV', label: 'Разработка / Боты / Скрипты', icon: '💻', color: 'bg-indigo-100 text-indigo-700' },
    { value: 'OTHER', label: 'Другое', icon: '📁', color: 'bg-slate-100 text-slate-700' },
  ];

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [description]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedCategory = categoriesList.find(cat => cat.value === category) || categoriesList[0];

  // 🆕 Логика добавления ника в тег
  const handleAddUsername = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const name = currentUsernameInput.trim();
      if (name && !whitelistUsernames.includes(name) && name.length >= 3) {
        setWhitelistUsernames([...whitelistUsernames, name]);
        setCurrentUsernameInput('');
        setWhitelistErrors([]); // Сбрасываем ошибки при успешном добавлении
      }
    }
  };

  const removeUsername = (indexToRemove) => {
    setWhitelistUsernames(whitelistUsernames.filter((_, index) => index !== indexToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setWhitelistErrors([]);
    let taskId = null;

    try {
      const parsedQuantity = parseInt(quantity, 10) || 1;

      // 1. Сначала создаем задачу
      const response = await api.post('tasks/', {
        title,
        description,
        price: parseFloat(price),
        quantity: parsedQuantity,
        max_attempts_per_user: onePerExecutor ? 1 : parsedQuantity,
        category,
        deadline_hours: parseInt(deadlineHours, 10) || 1
      });

      taskId = response.data.id; // Получаем ID созданной задачи

      // 2. Если есть ники в белом списке, добавляем их по очереди
      if (whitelistUsernames.length > 0 && taskId) {
        const errors = [];
        for (const username of whitelistUsernames) {
          try {
            await api.post(`tasks/${taskId}/whitelist/`, { username });
          } catch (err) {
            const errorMsg = err.response?.data?.error || `Не удалось добавить ${username}`;
            errors.push(errorMsg);
          }
        }
        if (errors.length > 0) {
          setWhitelistErrors(errors);
        }
      }

      alert('Заказ успешно опубликован!');
      // Сброс формы
      setTitle('');
      setDescription('');
      setPrice('');
      setQuantity(1);
      setCategory('AI');
      setDeadlineHours(1);
      setOnePerExecutor(true);
      setWhitelistUsernames([]);
      setWhitelistErrors([]);

      if (onTaskCreated) {
        onTaskCreated();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Ошибка при создании заказа';
      alert(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const parsedPrice = parseFloat(price) || 0;
  const parsedQuantity = parseInt(quantity, 10) || 1;
  const totalPrice = (parsedPrice * parsedQuantity).toFixed(2);

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Helmet>
        <title>
          {title.trim() 
            ? `Создание заказа: ${title.slice(0, 30)}... — RealWork.pro` 
            : 'Разместить заказ — RealWork.pro'}
        </title>
      </Helmet>

      <div>
        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Название задачи</label>
        <input 
          type="text" 
          value={title} 
          onChange={e => setTitle(e.target.value)}
          className="w-full px-4 py-3 bg-slate-50 text-slate-900 border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50 transition"
          placeholder="Например: Сгенерировать ИИ-аватара для промо-ролика"
          required 
          disabled={isSubmitting}
        />
      </div>

      <div>
        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Подробное техническое задание</label>
        <textarea 
          ref={textareaRef}
          value={description} 
          onChange={e => setDescription(e.target.value)}
          rows="4"
          className="w-full px-4 py-3 bg-slate-50 text-slate-900 border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50 transition resize-none overflow-y-auto"          style={{ minHeight: '120px', maxHeight: '500px' }}
          placeholder="Опишите, что именно нужно сделать исполнителю..."
          required 
          disabled={isSubmitting}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Оплата за 1 место (USDT)</label>
          <input 
            type="number" 
            value={price} 
            onChange={e => setPrice(e.target.value)}
            className="w-full px-4 py-3 bg-slate-50 text-slate-900 border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50 transition"
            placeholder="10"
            min="0.01"
            step="any"
            required 
            disabled={isSubmitting}
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Всего мест / выполнений</label>
          <input 
            type="number" 
            value={quantity} 
            onChange={e => setQuantity(e.target.value)}
            min="1"
            max="50"
            className="w-full px-4 py-3 bg-slate-50 text-slate-900 border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50 transition"
            required 
            disabled={isSubmitting}
          />
        </div>

        <div className="relative" ref={dropdownRef}>
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Категория</label>
          <button
            type="button"
            disabled={isSubmitting}
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 text-slate-900 border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50 transition text-left cursor-pointer"
          >
            <span className="flex items-center gap-2 min-w-0">
              <span className={`w-6 h-6 flex items-center justify-center rounded-lg ${selectedCategory.color} text-sm shrink-0`}>
                {selectedCategory.icon}
              </span>
              <span className="truncate">{selectedCategory.label}</span>
            </span>
            <svg className={`fill-current h-4 w-4 text-slate-500 transition-transform duration-200 shrink-0 ${isOpen ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
            </svg>
          </button>

          {isOpen && (
            <div className="absolute z-50 bottom-full mb-2 right-0 w-[280px] max-h-60 overflow-y-auto bg-white border border-slate-150 rounded-xl shadow-2xl py-1 animate-in fade-in slide-in-from-bottom-2 duration-150">
              {categoriesList.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => {
                    setCategory(cat.value);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition text-left hover:bg-slate-50 cursor-pointer ${
                    category === cat.value ? 'bg-indigo-50/50 text-indigo-900 font-medium' : 'text-slate-700'
                  }`}
                >
                  <span className={`w-7 h-7 flex items-center justify-center rounded-lg ${cat.color} text-base shrink-0`}>
                    {cat.icon}
                  </span>
                  <span className="whitespace-nowrap pr-2">{cat.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Дедлайн (часов)</label>
          <input 
            type="number" 
            value={deadlineHours} 
            onChange={e => setDeadlineHours(e.target.value)}
            min="1"
            className="w-full px-4 py-3 bg-slate-50 text-slate-900 border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50 transition"
            required 
            disabled={isSubmitting}
          />
        </div>
      </div>

      {/* 🆕 СЕКЦИЯ БЕЛОГО СПИСКА */}
      <div>
        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
          Белый список исполнителей <span className="text-slate-300 font-normal normal-case">(необязательно)</span>
        </label>
        <div className="flex flex-wrap gap-2 p-3 bg-slate-50 border border-slate-200 rounded-xl focus-within:ring-4 focus-within:ring-indigo-50 transition min-h-[52px] items-center">
          {whitelistUsernames.map((name, index) => (
            <span key={index} className="flex items-center gap-1.5 bg-indigo-100 text-indigo-700 px-3 py-1.5 rounded-lg text-sm font-semibold">
              @{name}
              <button 
                type="button" 
                onClick={() => removeUsername(index)} 
                className="hover:text-indigo-900 ml-1 transition"
                disabled={isSubmitting}
              >
                ✕
              </button>
            </span>
          ))}
          <input 
            type="text" 
            value={currentUsernameInput}
            onChange={(e) => setCurrentUsernameInput(e.target.value.replace(/\s/g, ''))} 
            onKeyDown={handleAddUsername}
            placeholder={whitelistUsernames.length === 0 ? "Введите ник и нажмите Enter..." : ""}
            className="flex-1 min-w-[150px] px-4 py-3 bg-slate-50 text-slate-900 border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50 transition text-sm"
            disabled={isSubmitting}
          />
        </div>
        <p className="text-xs text-slate-500 mt-1.5">
          💡 Эти пользователи увидят задачу в ленте первыми с пометкой <span className="text-amber-600 font-bold">⭐ Приглашение</span>.
        </p>
        
        {/* 🆕 Отображение ошибок добавления в белый список */}
        {whitelistErrors.length > 0 && (
          <div className="mt-2 p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700">
            <p className="font-bold mb-1">Не удалось добавить некоторых пользователей:</p>
            <ul className="list-disc list-inside space-y-1">
              {whitelistErrors.map((err, idx) => (
                <li key={idx}>{err}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {parsedQuantity > 1 && (
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between gap-3">
          <div className="space-y-0.5">
            <label htmlFor="onePerExecutor" className="text-sm font-semibold text-slate-800 cursor-pointer">
              Разные исполнители
            </label>
            <p className="text-xs text-slate-500">
              Один человек сможет сделать задачу только 1 раз
            </p>
          </div>
          <input 
            type="checkbox" 
            id="onePerExecutor"
            checked={onePerExecutor}
            onChange={e => setOnePerExecutor(e.target.checked)}
            className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
            disabled={isSubmitting}
          />
        </div>
      )}

      {parsedPrice > 0 && parsedQuantity > 1 && (
        <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-xl text-xs text-indigo-800">
          💡 Мест для выполнения: <b>{parsedQuantity}</b> {onePerExecutor ? '(каждый исполнитель делает по 1 разу)' : ''}. <br />
          Итого к списанию с баланса: <b>{totalPrice} USDT</b>
        </div>
      )}

      <button 
        type="submit" 
        disabled={isSubmitting}
        className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-xl font-semibold shadow-lg shadow-indigo-100 transition-all active:scale-[0.98] mt-4 cursor-pointer flex items-center justify-center gap-2"
      >
        {isSubmitting ? (
          <>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span>Публикация...</span>
          </>
        ) : (
          <span>🚀 Опубликовать заказ{parsedQuantity > 1 ? ` (${parsedQuantity} мест)` : ''} на бирже</span>
        )}
      </button>
    </form>
  );
}

export default CreateTaskForm;